import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "./useAuth";
import {
	fetchWorkflowAccessRequest,
	selectWorkflowAccess,
	selectWorkflowAccessLoaded,
	selectWorkflowAccessLoading,
	selectWorkflowAccessError,
} from "../store/slices/database/workflowAccessSlice";

// Pure check, kept separate so it can be reused outside React.
export const canUseWorkflowPermission = (workflows, permissionCode, processDefinitionKey) => {
	const workflow = workflows.find((w) => w.processDefinitionKey === processDefinitionKey);
	return Boolean(workflow?.permissions?.includes(permissionCode));
};

/**
 * Workflow access for the selected company, for the workflow pages (list, history, pending tasks).
 *
 * Stale-while-revalidate: every page that uses this refetches on mount, but cached data from an
 * earlier visit stays on screen meanwhile, so only the first visit (per company) waits.
 *
 *   isLoading    - first load, nothing cached yet: show the page loader
 *   isRefreshing - cached data shown, a background refresh is running: show a small spinner
 *   canUse(permissionCode, processDefinitionKey) - e.g. canUse("approve", task.processDefinitionKey)
 */
export const useWorkflowAccess = () => {
	const dispatch = useDispatch();
	const companyId = useSelector((state) => state.selectedCompany.data?.companyData?.companyId);
	// Only when logged in: the selected company is remembered across sessions, and a request made while
	// logged out (e.g. a public services page) sends the whole page to the Keycloak login.
	const { isAuthenticated } = useAuth();

	const workflows = useSelector(selectWorkflowAccess);
	const loaded = useSelector(selectWorkflowAccessLoaded);
	const loading = useSelector(selectWorkflowAccessLoading);
	const error = useSelector(selectWorkflowAccessError);

	useEffect(() => {
		if (companyId && isAuthenticated) {
			dispatch(fetchWorkflowAccessRequest({ companyId }));
		}
	}, [dispatch, companyId, isAuthenticated]);

	const canUse = useCallback(
		(permissionCode, processDefinitionKey) => canUseWorkflowPermission(workflows, permissionCode, processDefinitionKey),
		[workflows]
	);

	return {
		workflows,
		canUse,
		isLoading: loading && !loaded,
		isRefreshing: loading && loaded,
		error,
	};
};
